'use client';

import { useEffect, useRef, useState } from 'react';

import { BOOK_PRESENTATION } from '@sharpr/ui/books';
import { useBookCatalogContext } from '@sharpr/ui';

// The books on the strip are the presentation entries that declare a `landing` logo, in declaration order;
// a per-logo `heightClass` there overrides the default 'h-7 md:h-8'. A logo's alt text is its book's catalog name.
export const sportsbookLogos: Array<{ key: string; src: string; heightClass?: string }> =
  Object.entries(BOOK_PRESENTATION).flatMap(([key, p]) =>
    p.landing ? [{ key, src: p.landing.src, heightClass: p.landing.heightClass }] : [],
  );

export function SportsbookStrip() {
  const catalog = useBookCatalogContext();
  const loop = [...sportsbookLogos, ...sportsbookLogos];
  // Only run the marquee while the banner is in view — preloaded logos have
  // time to land before the first frame paints, and it saves compositing cost
  // while offscreen.
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: '100px 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-label="Supported sportsbooks and exchanges"
      className="relative py-3 md:py-8"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-center text-[11px] font-semibold tracking-[0.2em] uppercase text-fg/40 mb-4">
          Sharpr plays at
        </p>

        <div className="relative overflow-hidden">
          <div
            className="animate-marquee flex items-center gap-10 md:gap-16 w-max"
            style={{
              willChange: 'transform',
              animationPlayState: inView ? 'running' : 'paused',
            }}
          >
            {loop.map((logo, i) => {
              const isFirstCopy = i < sportsbookLogos.length;
              const heightClass = logo.heightClass ?? 'h-7 md:h-8';
              return (
                // eslint-disable-next-line @next/next/no-img-element -- local logos (mostly SVG, which next/image serves unoptimized); a plain img costs nothing and no optimization usage
                <img
                  key={`${logo.key}-${i}`}
                  src={logo.src}
                  alt={catalog.get(logo.key)?.name ?? logo.key}
                  width={160}
                  height={32}
                  loading="eager"
                  decoding="async"
                  fetchPriority={isFirstCopy ? 'high' : 'auto'}
                  className={`${heightClass} w-auto max-w-32 md:max-w-40 object-contain shrink-0`}
                />
              );
            })}
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-16"
            style={{
              background:
                'linear-gradient(to right, hsl(var(--bg)), transparent)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-16"
            style={{
              background:
                'linear-gradient(to left, hsl(var(--bg)), transparent)',
            }}
          />
        </div>
      </div>
    </section>
  );
}
